import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Stack,
  useToast,
} from '@chakra-ui/react';
import { usePutSubAccountMutation } from 'api';
import { AccountResponse } from 'api/bindings/AccountResponse';
import { PutSubAccountRequest } from 'api/bindings/PutSubAccountRequest';
import { SubAccountResponse } from 'api/bindings/SubAccountResponse';
import { AppInput, FormButtonGroup } from 'features';
import { useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

export interface EditSubAccountDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  account: AccountResponse;
  subAccount: SubAccountResponse;
}

interface Form extends PutSubAccountRequest {
  id: string;
  accountId: string;
}

export const EditSubAccountDrawer = (props: EditSubAccountDrawerProps) => {
  const { account, subAccount, ...rest } = props;

  // Fetches
  const [putSubAccount] = usePutSubAccountMutation();

  // State
  const toast = useToast();
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      id: subAccount.subAccountId,
      accountId: subAccount.accountId,
      code: subAccount.subAccountCode,
      name: subAccount.subAccountName,
      description: subAccount.subAccountDescription,
    },
  });
  const { reset, watch, handleSubmit, control } = form;
  const [subAccountCode, subAccountName] = watch(['code', 'name']);
  const fullSubAccountCode = useMemo(
    () => `${account.code}.${subAccountCode}`,
    [account.code, subAccountCode]
  );
  const fullSubAccountName = useMemo(
    () => `${fullSubAccountCode} ${subAccountName}`,
    [fullSubAccountCode, subAccountName]
  );

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await putSubAccount(form);
        if ('error' in result) {
          toast({
            title: `Failed to update ${fullSubAccountName}`,
            description: (result.error as any)?.data?.message,
            status: 'error',
          });
          return;
        }
      } catch {}
      toast({
        title: `Account ${fullSubAccountName} posted!`,
        status: 'success',
      });
      reset();
      rest.onClose();
    },
    [fullSubAccountName, putSubAccount, reset, rest, toast]
  );

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Drawer {...rest}>
          <DrawerOverlay />
          <DrawerCloseButton />
          <DrawerContent>
            <DrawerHeader>Edit Account: {fullSubAccountName}</DrawerHeader>
            <DrawerBody>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="code">Code:</FormLabel>
                  <Controller
                    control={control}
                    name="code"
                    rules={{
                      required: {
                        value: true,
                        message: `Account code is required.`,
                      },
                      min: {
                        value: 0,
                        message: `Account code must be zero or higher.`,
                      },
                      pattern: {
                        value: /^\d+$/,
                        message: 'Account code must be numeral.',
                      },
                    }}
                    render={(f) => (
                      <AppInput type="number" id="code" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="name">Name:</FormLabel>
                  <Controller
                    control={control}
                    name="name"
                    rules={{
                      required: {
                        value: true,
                        message: `Account name is required.`,
                      },
                      minLength: {
                        value: 3,
                        message: `Account name must be at least 3 characters.`,
                      },
                      maxLength: {
                        value: 256,
                        message: `Account name must be no more than 256 characters.`,
                      },
                    }}
                    render={(f) => <AppInput id="name" {...(f.field as any)} />}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="description">Description:</FormLabel>
                  <Controller
                    control={control}
                    name="description"
                    rules={{
                      required: {
                        value: true,
                        message: 'Account description is required.',
                      },
                      maxLength: {
                        value: 256,
                        message: `Description must be no more than 256 characters.`,
                      },
                    }}
                    render={(f) => (
                      <AppInput id="description" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onConfirm={handleSubmit(onSubmit)}
                onCancel={() => {
                  reset();
                  rest.onClose();
                }}
              />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </FormProvider>
  );
};
